/**
 * PolishReviewCard — Collapsible card showing one agent's review and suggestions.
 */

import React, { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui';
import { LyricScoreBar } from './LyricScoreBar';
import { SuggestionDiff } from './SuggestionDiff';
import type { PolishReview } from '@/types/lyricOrchestrator.types';
import type { LyricAgent } from '@/types/lyricOrchestrator.types';

interface PolishReviewCardProps {
  review: PolishReview;
  agent: LyricAgent;
}

const IconLookup = LucideIcons as unknown as Record<string, React.FC<{ size?: number; className?: string }>>;

export const PolishReviewCard: React.FC<PolishReviewCardProps> = ({ review, agent }) => {
  const [isOpen, setIsOpen] = useState(false);

  const IconComponent = IconLookup[agent.icon] || LucideIcons.HelpCircle;
  const borderColor = agent.color.replace('text-', 'border-');

  const changeCount = review.suggestions.filter(s => s.changeType !== 'keep').length;

  return (
    <div className={`border-l-4 ${borderColor} bg-slate-900/40 rounded-lg overflow-hidden`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 hover:bg-slate-800/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <IconComponent size={16} className={agent.color} />
          <span className="text-sm font-bold text-slate-200">{agent.name}</span>
          <Badge variant="default-muted" size="xs">{review.scores.overall}/10</Badge>
          <span className="text-[10px] text-slate-500">
            conf: {(review.confidence * 100).toFixed(0)}%
          </span>
          {changeCount > 0 && (
            <Badge variant="warning-muted" size="xs">{changeCount} changes</Badge>
          )}
        </div>
        <ChevronDown
          size={14}
          className={`text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="px-3 pb-4 space-y-4 animate-in slide-in-from-top-2 duration-200">
          {/* Overall Assessment */}
          {review.overallAssessment && (
            <p className="text-[11px] text-slate-400 italic border-l-2 border-slate-700 pl-2">
              {review.overallAssessment}
            </p>
          )}

          {/* Score bars */}
          <div className="space-y-1.5">
            <LyricScoreBar label="Structure" score={review.scores.structure} color="bg-emerald-500" />
            <LyricScoreBar label="Hook" score={review.scores.hook} color="bg-rose-500" />
            <LyricScoreBar label="Imagery" score={review.scores.imagery} color="bg-violet-500" />
            <LyricScoreBar label="Authenticity" score={review.scores.authenticity} color="bg-amber-500" />
            <LyricScoreBar label="Emotional" score={review.scores.emotionalImpact} color="bg-cyan-500" />
            <LyricScoreBar label="Overall" score={review.scores.overall} color="bg-amber-400" />
          </div>

          {/* Strengths / Weaknesses */}
          <div className="grid grid-cols-2 gap-3">
            {review.strengths.length > 0 && (
              <div>
                <h5 className="text-[10px] font-black text-green-400 uppercase tracking-widest mb-1">Strengths</h5>
                <ul className="space-y-0.5">
                  {review.strengths.map((s, i) => (
                    <li key={i} className="text-[11px] text-slate-400">+ {s}</li>
                  ))}
                </ul>
              </div>
            )}
            {review.weaknesses.length > 0 && (
              <div>
                <h5 className="text-[10px] font-black text-red-400 uppercase tracking-widest mb-1">Weaknesses</h5>
                <ul className="space-y-0.5">
                  {review.weaknesses.map((w, i) => (
                    <li key={i} className="text-[11px] text-slate-400">- {w}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Suggestions */}
          {review.suggestions.length > 0 && (
            <div>
              <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">
                Section Suggestions
              </h5>
              <div className="space-y-1.5">
                {review.suggestions.map((s, i) => (
                  <SuggestionDiff key={i} suggestion={s} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
